import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

interface ApplicantsContextType {
  selectedApplicantIds: string[];
  addApplicantId: (id: string) => void;
  removeApplicantId: (id: string) => void;
  clearApplicantIds: () => void;
  setApplicantIds: (ids: string[]) => void;
  isSelected: (id: string) => boolean;
  isLoading: boolean;
}

const ApplicantsContext = createContext<ApplicantsContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedApplicantIds';

// Check if localStorage is available
const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

// Helper function to load from localStorage
const loadFromStorage = (): string[] => {
  if (!isLocalStorageAvailable()) {
    return [];
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error('Error parsing stored applicant IDs:', error);
    localStorage.removeItem(STORAGE_KEY);
  }
  return [];
};

// Helper function to save to localStorage
const saveToStorage = (applicantIds: string[]): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(applicantIds));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const ApplicantsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedApplicantIds, setSelectedApplicantIds] = useState<string[]>(() => loadFromStorage());
  const [isLoading, setIsLoading] = useState(true);

  // Save to localStorage whenever selectedPersonIds changes
  useEffect(() => {
    if (!isLoading) {
      saveToStorage(selectedApplicantIds);
    }
  }, [selectedApplicantIds, isLoading]);

  // Mark as loaded after initial render
  useEffect(() => {
    setIsLoading(false);
  }, []);

  const addApplicantId = useCallback((id: string) => {
    setSelectedApplicantIds(prev => {
      // Check if person ID already exists
      if (prev.includes(id)) {
        return prev;
      }
      return [...prev, id];
    });
  }, []);

  const removeApplicantId = useCallback((id: string) => {
    setSelectedApplicantIds(prev => prev.filter(applicantId => applicantId !== id));
  }, []);

  const clearApplicantIds = useCallback(() => {
    setSelectedApplicantIds([]);
  }, []);

  const setApplicantIds = useCallback((ids: string[]) => {
    setSelectedApplicantIds(ids);
  }, []);

  const isSelected = useCallback((id: string) => {
    return selectedApplicantIds.includes(id);
  }, [selectedApplicantIds]);

  return (
    <ApplicantsContext.Provider 
      value={{ 
        selectedApplicantIds, 
        addApplicantId, 
        removeApplicantId,
        clearApplicantIds,
        setApplicantIds,
        isSelected,
        isLoading
      }}
    >
      {children}
    </ApplicantsContext.Provider>
  );
};

export const useApplicants = () => {
  const context = useContext(ApplicantsContext);
  if (context === undefined) {
    throw new Error('useApplicants must be used within an ApplicantsProvider');
  }
  return context;
}; 